import { Component } from '@theme/component';
import { isDesktopBreakpoint } from '@theme/utilities';

/**
 * @typedef {object} VariantData
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {string} price_formatted
 * @property {number|null} compare_at_price
 * @property {string|null} compare_at_price_formatted
 * @property {string[]} options
 */

/**
 * Sticky CTA Component
 * Shows/hides a sticky bar when the main buy buttons scroll out of view.
 * Position (top/bottom) is controlled via CSS classes set in the section settings.
 *
 * @extends Component
 */
class StickyCtaComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #buyButtonsOutOfView = false;

  /** @type {boolean} */
  #footerInView = false;

  /** @type {boolean} */
  #entranceAnimationPlayed = false;

  /** @type {VariantData[]} */
  #variants = [];

  connectedCallback() {
    super.connectedCallback();

    // Initialize entrance animation state
    this.setAttribute('data-entrance-played', 'false');

    // Create abort controller for event listener cleanup
    this.#abortController = new AbortController();

    // Set up quantity selector event listeners
    this.#setupQuantitySelector();

    // Set up variant selector event listeners
    this.#setupVariantSelectors();

    // Listen for variant updates from the main PDP (cross-section sync)
    document.addEventListener('variant:update', this.#onMainVariantUpdate, {
      signal: this.#abortController.signal,
    });

    // Set up intersection observers to watch buy buttons and footer
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.#setupIntersectionObserver();
      this.#setupFooterObserver();
    });
  }

  /**
   * Sets up quantity selector increment/decrement buttons
   */
  #setupQuantitySelector() {
    const quantityInput = this.refs?.quantityInput;
    const decrementButton = this.refs?.decrementButton;
    const incrementButton = this.refs?.incrementButton;
    const signal = this.#abortController?.signal;

    if (!quantityInput) return;

    // Increment button
    if (incrementButton) {
      incrementButton.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        quantityInput.value = currentValue + 1;
      }, { signal });
    }

    // Decrement button
    if (decrementButton) {
      decrementButton.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      }, { signal });
    }

    // Prevent invalid values
    quantityInput.addEventListener('input', () => {
      const value = parseInt(quantityInput.value);
      if (isNaN(value) || value < 1) {
        quantityInput.value = 1;
      }
    }, { signal });
  }

  /**
   * Sets up variant selector change listeners and parses variant data
   */
  #setupVariantSelectors() {
    const variantsScript = this.querySelector('script[data-sd-sticky-cta-variants]');
    if (!variantsScript?.textContent) return;

    try {
      this.#variants = JSON.parse(variantsScript.textContent);
    } catch {
      return;
    }

    const signal = this.#abortController?.signal;
    const selects = this.querySelectorAll('.sd-sticky-cta__variant-select');

    for (const select of selects) {
      select.addEventListener('change', () => this.#onVariantSelectChange(), { signal });
    }
  }

  /**
   * Handles variant select changes - finds the matching variant and updates
   * the hidden variant ID input, button state, and price display.
   */
  #onVariantSelectChange() {
    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      this.querySelectorAll('.sd-sticky-cta__variant-select')
    );

    // Build a map of option position → selected value
    /** @type {Map<number, string>} */
    const selectedOptions = new Map();
    for (const select of selects) {
      const position = parseInt(select.dataset.optionPosition || '0');
      if (position > 0) {
        selectedOptions.set(position, select.value);
      }
    }

    // Find matching variant
    const variant = this.#variants.find(v => {
      for (const [position, value] of selectedOptions) {
        if (v.options[position - 1] !== value) return false;
      }
      return true;
    });

    // Update hidden variant ID input
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput) {
      variantInput.value = variant ? String(variant.id) : '';
    }

    this.#updateButton(variant);
    this.#updatePrice(variant);
  }

  /**
   * Syncs the sticky CTA when the main PDP variant picker changes.
   * Listens on document since the event bubbles from a different section.
   * @param {Event} event
   */
  #onMainVariantUpdate = (event) => {
    const detail = /** @type {{ resource: any, data: { productId: string } }} */ (event).detail;

    // Only respond to events for the same product
    if (!detail?.data?.productId || detail.data.productId !== this.dataset.productId) return;

    const variant = detail.resource;
    if (!variant?.id) {
      // Variant is null (no match) — disable button
      this.#updateButton(undefined);
      return;
    }

    // Find matching variant in our embedded data (has formatted prices)
    const variantId = Number(variant.id);
    const matchedVariant = this.#variants.find(v => v.id === variantId);
    if (!matchedVariant) {
      this.#updateButton(undefined);
      return;
    }

    // Update select dropdowns to reflect the new selection
    // (silently skips if the option value was filtered out as unavailable)
    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      this.querySelectorAll('.sd-sticky-cta__variant-select')
    );
    for (const select of selects) {
      const position = parseInt(select.dataset.optionPosition || '0');
      if (position > 0 && matchedVariant.options[position - 1] != null) {
        select.value = matchedVariant.options[position - 1];
      }
    }

    // Update hidden variant ID input
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput) {
      variantInput.value = String(matchedVariant.id);
    }

    this.#updateButton(matchedVariant);
    this.#updatePrice(matchedVariant);
  };

  /**
   * Updates button disabled state and text based on the selected variant.
   * @param {VariantData | undefined} variant
   */
  #updateButton(variant) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('.sd-sticky-cta__button'));
    const buttonText = this.querySelector('.sd-sticky-cta__button-text');
    if (!button || !buttonText) return;

    if (!variant || !variant.available) {
      button.disabled = true;
      buttonText.textContent = this.dataset.soldOutText ?? '';
    } else {
      button.disabled = false;
      const showPrice = this.dataset.showPriceInButton === 'true';
      const customText = this.dataset.customButtonText;
      const addToCartText = this.dataset.addToCartText ?? '';
      const baseText = customText || addToCartText;

      buttonText.textContent = showPrice
        ? `${baseText}-${variant.price_formatted}`
        : baseText;
    }
  }

  /**
   * Updates price display elements based on the selected variant.
   * @param {VariantData | undefined} variant
   */
  #updatePrice(variant) {
    if (!variant) return;

    const priceElements = this.querySelectorAll('.sd-sticky-cta__price');
    const compareElements = this.querySelectorAll('.sd-sticky-cta__compare-price');
    const isOnSale = variant.compare_at_price != null && variant.compare_at_price > variant.price;
    const saleLabel = this.dataset.saleLabel || '';
    const showCompare = this.dataset.showCompareAtPrice === 'true';

    for (const priceEl of priceElements) {
      priceEl.classList.toggle('sd-sticky-cta__price--on-sale', isOnSale);
      priceEl.textContent = isOnSale
        ? `${saleLabel} ${variant.price_formatted}`
        : variant.price_formatted;
    }

    for (const compareEl of compareElements) {
      if (isOnSale && showCompare && variant.compare_at_price_formatted) {
        const compareLabel = this.dataset.compareAtPriceLabel ?? '';
        compareEl.textContent = compareLabel
          ? `${compareLabel} ${variant.compare_at_price_formatted}`
          : variant.compare_at_price_formatted;
        /** @type {HTMLElement} */ (compareEl).style.display = '';
      } else {
        /** @type {HTMLElement} */ (compareEl).style.display = 'none';
      }
    }

    // Announce price change once via the shared live region
    const liveRegion = this.refs?.liveRegion;
    if (liveRegion) {
      const announcement = isOnSale && showCompare && variant.compare_at_price_formatted
        ? `${saleLabel} ${variant.price_formatted}, ${this.dataset.compareAtPriceLabel ?? ''} ${variant.compare_at_price_formatted}`
        : variant.price_formatted;
      liveRegion.textContent = announcement;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    if (this.#footerObserver) {
      this.#footerObserver.disconnect();
      this.#footerObserver = null;
    }

    this.#updateScrollPadding(false);
  }

  /**
   * Sets up the Intersection Observer to watch the main buy buttons.
   * Shows the sticky CTA when buy buttons are out of view.
   */
  #setupIntersectionObserver() {
    // Find the main buy buttons block on the product page
    const buyButtonsBlock =
      document.querySelector('.buy-buttons-block') ||
      document.querySelector('[class*="buy-button"]') ||
      document.querySelector('product-form-component');

    if (!buyButtonsBlock) {
      return;
    }

    // Get threshold from data attribute (default to 0.1 if not set)
    const threshold = parseFloat(this.dataset.threshold) || 0.1;

    // Create intersection observer
    // When the buy buttons are less than threshold visible, show the sticky CTA
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Only treat buy buttons as "out of view" when they've scrolled
          // ABOVE the viewport (user scrolled past them). When they're below
          // the fold (e.g. initial load on small screens), keep CTA hidden.
          const isAboveViewport = entry.boundingClientRect.top < 0;
          this.#buyButtonsOutOfView = !entry.isIntersecting && isAboveViewport;
          this.#updateVisibility();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: threshold,
      }
    );

    // Start observing the buy buttons
    this.#observer.observe(buyButtonsBlock);
  }

  /**
   * Sets up an Intersection Observer on the footer to hide the sticky CTA
   * before it overlaps, triggering the hide animation.
   */
  #setupFooterObserver() {
    const footer = document.querySelector('.sd-footer-section');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#footerInView = entry.isIntersecting;
          this.#updateVisibility();
        }
      },
      {
        root: null,
        // Trigger slightly before the footer enters the viewport so the
        // hide animation completes before the CTA would visually overlap.
        rootMargin: '50px 0px 0px 0px',
        threshold: 0,
      }
    );

    this.#footerObserver.observe(footer);
  }

  /**
   * Determines CTA visibility from both observers:
   * visible only when buy buttons are out of view AND footer is not in view.
   */
  #updateVisibility() {
    const shouldShow = this.#buyButtonsOutOfView && !this.#footerInView;

    if (shouldShow !== this.#isVisible) {
      this.#isVisible = shouldShow;
      this.#toggleVisibility(shouldShow);
    }
  }

  /**
   * Toggles the visibility of the sticky CTA
   * @param {boolean} visible - Whether to show or hide
   */
  #toggleVisibility(visible) {
    if (visible) {
      this.setAttribute('data-visible', 'true');
      this.removeAttribute('hidden');

      // Mark entrance animation as played after it completes (once only)
      if (!this.#entranceAnimationPlayed) {
        this.#entranceAnimationPlayed = true;
        const entranceAnimation = this.dataset.entranceAnimation;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (entranceAnimation && entranceAnimation !== 'none' && !prefersReducedMotion) {
          this.addEventListener('animationend', () => {
            this.setAttribute('data-entrance-played', 'true');
          }, { once: true });
        } else {
          this.setAttribute('data-entrance-played', 'true');
        }
      }
    } else {
      this.setAttribute('data-visible', 'false');
    }

    this.#updateScrollPadding(visible);
  }

  /**
   * Keeps keyboard focus clear of the bar (WCAG 2.4.11 Focus Not Obscured).
   * Publishes the room the bar takes at its current edge — its resolved `top` or
   * `bottom` (which includes Horizon's sticky header while that shows) plus its own
   * height — as `--sd-sticky-cta-reserve-start` / `-end` on <html>, where the
   * stylesheet turns it into scroll padding. Read once per show, not observed: the
   * padding only has to be big enough, so the header hiding mid-show just leaves a
   * little extra room. The docked promo band is left out because it grows and
   * shrinks while the bar shows; the tracker's stylesheet reserves it per state.
   * @param {boolean} visible - Whether the bar is showing
   */
  #updateScrollPadding(visible) {
    const root = document.documentElement;
    root.style.removeProperty('--sd-sticky-cta-reserve-start');
    root.style.removeProperty('--sd-sticky-cta-reserve-end');
    if (!visible) return;

    const position = isDesktopBreakpoint() ? this.dataset.desktopPosition : this.dataset.mobilePosition;
    if (position !== 'top' && position !== 'bottom') return;

    /** @type {HTMLElement | null} */
    const dockedBand = this.querySelector('.sd-promotion-tracker--docked');
    const barHeight = this.offsetHeight - (dockedBand?.offsetHeight ?? 0);
    const edgeOffset = parseFloat(getComputedStyle(this)[position]) || 0;
    const edge = position === 'top' ? 'start' : 'end';
    root.style.setProperty(`--sd-sticky-cta-reserve-${edge}`, `${Math.ceil(edgeOffset + barHeight)}px`);
  }
}

if (!customElements.get('sd-sticky-cta-component')) {
  customElements.define('sd-sticky-cta-component', StickyCtaComponent);
}
